import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Checkbox, Drawer, Icon, Loading, Pills, Section, Text } from 'barte-design-system';
import { CaretDown, CheckCircle, DeviceMobileSlash, DeviceRotate, MagnifyingGlass } from '@phosphor-icons/react';
import './DrawerDetalhesTerminal.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TerminalDetails {
  id: string;
  serialNumber: string;
  status: 'Alocada' | 'Em estoque' | 'Em manutenção' | 'Desativada';
  payfac: string;
  payfacId: string;
  owner: 'Barte' | 'Própria';
  seller: string;
  sellerId: string;
  lastTransaction: string;
  vinculationDate: string;
  posVersion: string;
  terminalId: string;
  model: string;
}

interface TimelineEvent {
  type: 'vinculado' | 'desvinculado';
  date: string;
  time: string;
  actor: string;
}

interface DrawerDetalhesTerminalProps {
  isOpen: boolean;
  onClose: () => void;
  terminal: TerminalDetails | null;
  initialStatusAction?: StatusAction;
}

// ─── Mock data helpers ────────────────────────────────────────────────────────

type PillsStateValue = 'success' | 'info' | 'warning' | 'error' | 'default';

const STATUS_PILL: Record<string, PillsStateValue> = {
  'Alocada':        'success',
  'Em estoque':     'warning',
  'Em manutenção':  'error',
  'Desativada':     'default',
};

const STATUS_LABEL: Record<string, string> = {
  'Alocada':        'Alocada Lojista',
  'Em estoque':     'Em estoque',
  'Em manutenção':  'Em manutenção',
  'Desativada':     'Desativada',
};

type StatusAction = 'desassociar' | 'associar-outro' | null;

// Mock CNPJ por payfac
const PAYFAC_CNPJ: Record<string, string> = {
  'Barte':      '12.345.678/0001-90',
  'FinTech BR': '09.898.789/0001-09',
  'PayNow BR':  '34.567.890/0001-12',
  '—':          '—',
};

// Mock seller extra data
const SELLER_RAZAO_SOCIAL: Record<string, string> = {
  'Loja Central LTDA':       'Loja Central Comércio e Serviços LTDA',
  'Mercado Bom Preço':       'Mercado Bom Preço Supermercados LTDA',
  'Restaurante Sabor Bom':   'Sabor Bom Restaurantes ME',
  'Posto Combustível Star':  'Star Combustíveis e Derivados LTDA',
  'Academia FitLife':        'FitLife Academia e Bem-Estar LTDA',
  'Padaria Pão Quente':      'Pão Quente Padaria e Confeitaria ME',
  'TechStore Informática':   'TechStore Comércio de Eletrônicos LTDA',
  'Supermercado Família':    'Família Supermercados e Atacado LTDA',
  'Farmácia Saúde & Vida':   'Saúde & Vida Drogarias LTDA',
  'Livraria Cultura':        'Cultura Livraria e Entretenimento SA',
  'Ótica Visão Clara':       'Visão Clara Ótica e Optometria LTDA',
  'Pet Shop Animal Love':    'Animal Love Pet Shop e Veterinária ME',
};

const SELLER_NOME_FANTASIA: Record<string, string> = {
  'Loja Central LTDA':       'Loja Central',
  'Mercado Bom Preço':       'Bom Preço',
  'Restaurante Sabor Bom':   'Sabor Bom',
  'Posto Combustível Star':  'Star Posto',
  'Academia FitLife':        'FitLife',
  'Padaria Pão Quente':      'Pão Quente',
  'TechStore Informática':   'TechStore',
  'Supermercado Família':    'Família Supermercados',
  'Farmácia Saúde & Vida':   'Saúde & Vida',
  'Livraria Cultura':        'Livraria Cultura',
  'Ótica Visão Clara':       'Visão Clara',
  'Pet Shop Animal Love':    'Animal Love',
};

// Mock de histórico de vínculos anteriores do terminal (mais recente primeiro)
const OLDER_TIMELINE_MOCK: TimelineEvent[] = [
  { type: 'desvinculado', date: '20/04/2023', time: '11:40', actor: 'Livraria Cultura' },
  { type: 'vinculado',    date: '22/05/2022', time: '09:05', actor: 'Livraria Cultura' },
  { type: 'desvinculado', date: '15/02/2021', time: '14:22', actor: 'Farmácia Saúde & Vida' },
  { type: 'vinculado',    date: '30/08/2020', time: '10:15', actor: 'Farmácia Saúde & Vida' },
  { type: 'desvinculado', date: '10/11/2019', time: '16:08', actor: 'Restaurante Sabor Bom' },
  { type: 'vinculado',    date: '14/02/2018', time: '08:30', actor: 'Restaurante Sabor Bom' },
];

function buildTimelineEvents(terminal: TerminalDetails): TimelineEvent[] {
  if (terminal.status !== 'Alocada') return OLDER_TIMELINE_MOCK;
  const current: TimelineEvent = {
    type: 'vinculado',
    date: terminal.vinculationDate,
    time: '09:15',
    actor: terminal.seller,
  };
  return [current, ...OLDER_TIMELINE_MOCK];
}

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function monthYearLabel(date: string): string {
  const [, month, year] = date.split('/');
  return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${year}`;
}

// Mock sellers para busca
const MOCK_SELLERS: { name: string; id: string }[] = [
  { name: 'Guilherme Antônio',  id: '97881001' },
  { name: 'Guilherme Barros',   id: '97882002' },
  { name: 'Guilherme Cardoso',  id: '97887231' },
  { name: 'Guilherme Costa',    id: '97884004' },
  { name: 'Guilherme Torres',   id: '97885005' },
  { name: 'Ana Paula Silva',    id: '97886006' },
  { name: 'Bruno Ferreira',     id: '97887007' },
  { name: 'Carlos Mendes',      id: '97888008' },
  { name: 'Daniela Souza',      id: '97889009' },
  { name: 'Eduardo Lima',       id: '97890010' },
  { name: 'Fernanda Rocha',     id: '97891011' },
  { name: 'Gabriel Oliveira',   id: '97892012' },
];

// Mock de CNPJ do lojista, derivado do ID (não há campo de CNPJ no mock de sellers)
function sellerCnpjFor(id: string): string {
  return `${id.slice(0, 2)}.${id.slice(2, 5)}.${id.slice(5, 8)}/0001-90`;
}

// ─── Busca de seller ───────────────────────────────────────────────────────────

type SellerSearchType = 'lojista' | 'cnpj' | 'lojistaId';

const SELLER_SEARCH_TYPE_OPTIONS: { value: SellerSearchType; label: string; placeholder: string }[] = [
  { value: 'lojista',   label: 'Nome do Lojista', placeholder: 'Buscar por nome do lojista...' },
  { value: 'cnpj',      label: 'CNPJ',            placeholder: 'Buscar por CNPJ...'            },
  { value: 'lojistaId', label: 'Id do Lojista',   placeholder: 'Buscar por ID do lojista...'   },
];

function sellerFieldForSearchType(seller: { name: string; id: string }, type: SellerSearchType): string {
  if (type === 'cnpj') return sellerCnpjFor(seller.id);
  if (type === 'lojistaId') return seller.id;
  return seller.name;
}


// ─── Sub-componente: linha do tempo de vínculos ──────────────────────────────

function HistoryTimeline({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0) {
    return <Text variant="body-sm" className="ddt-info-label">Nenhum vínculo registrado.</Text>;
  }

  return (
    <div className="ddt-timeline">
      {events.map((event, i) => {
        const isCurrent = i === 0;
        const label = monthYearLabel(event.date);
        const showMonth = i === 0 || monthYearLabel(events[i - 1].date) !== label;
        const isLast = i === events.length - 1;

        return (
          <div className="ddt-timeline-row" key={`${event.date}-${event.time}-${i}`}>
            <div className="ddt-timeline-month">
              {showMonth && <Pills label={label} state="info" size="sm" />}
            </div>
            <div className="ddt-timeline-track">
              <span className={`ddt-timeline-dot${isCurrent ? ' ddt-timeline-dot--current' : ''}`} />
              {!isLast && <span className="ddt-timeline-line" />}
            </div>
            <div className="ddt-timeline-content">
              <Text
                variant="label-md"
                className={`ddt-timeline-title${isCurrent ? '' : ' ddt-timeline-title--muted'}`}
              >
                {event.type === 'vinculado' ? 'Terminal Vinculado' : 'Terminal Desvinculado'}
              </Text>
              <Text variant="body-sm" className="ddt-timeline-meta">
                {event.date} {event.time} • {event.actor}
              </Text>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DrawerDetalhesTerminal({ isOpen, onClose, terminal, initialStatusAction }: DrawerDetalhesTerminalProps) {
  const [statusAction, setStatusAction] = useState<StatusAction>(initialStatusAction ?? null);

  React.useEffect(() => {
    if (isOpen) setStatusAction(initialStatusAction ?? null);
  }, [isOpen, initialStatusAction]);

  // Desassociar
  const [confirmed, setConfirmed] = useState(false);
  const [desassociarStep, setDesassociarStep] = useState<'confirm' | 'loading' | 'success'>('confirm');

  // Associar a outro seller
  const [associarStep, setAssociarStep] = useState<'form' | 'loading' | 'success'>('form');
  const [sellerQuery, setSellerQuery] = useState('');
  const [sellerSearchType, setSellerSearchType] = useState<SellerSearchType>('lojista');
  const [sellerTypeDropdownOpen, setSellerTypeDropdownOpen] = useState(false);
  const sellerSearchBarRef = useRef<HTMLDivElement>(null);
  const [selectedSeller, setSelectedSeller] = useState<{ name: string; id: string } | null>(null);
  const [confirmedAssociar, setConfirmedAssociar] = useState(false);

  const currentSellerSearchType = SELLER_SEARCH_TYPE_OPTIONS.find((o) => o.value === sellerSearchType)!;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (sellerSearchBarRef.current && !sellerSearchBarRef.current.contains(e.target as Node)) {
        setSellerTypeDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sellerResults = sellerQuery.trim().length > 0
    ? MOCK_SELLERS.filter((s) =>
        sellerFieldForSearchType(s, sellerSearchType).toLowerCase().includes(sellerQuery.trim().toLowerCase()),
      )
    : [];

  function handleAssociar() {
    setAssociarStep('loading');
    setTimeout(() => setAssociarStep('success'), 2000);
  }

  function handleCloseAssociar() {
    setStatusAction(null);
    setAssociarStep('form');
    setSellerQuery('');
    setSellerSearchType('lojista');
    setSelectedSeller(null);
    setConfirmedAssociar(false);
    onClose();
  }

  function handleDesassociar() {
    setDesassociarStep('loading');
    setTimeout(() => setDesassociarStep('success'), 2000);
  }

  function handleCloseDesassociar() {
    setStatusAction(null);
    setConfirmed(false);
    setDesassociarStep('confirm');
    onClose();
  }

  if (!terminal) return null;

  const cnpj = PAYFAC_CNPJ[terminal.payfac] ?? '—';
  const razaoSocial = SELLER_RAZAO_SOCIAL[terminal.seller] ?? '—';
  const nomeFantasia = SELLER_NOME_FANTASIA[terminal.seller] ?? terminal.seller;
  const pillState = STATUS_PILL[terminal.status] ?? 'default';
  const pillLabel = STATUS_LABEL[terminal.status] ?? terminal.status;

  // ─── View: Confirmar desassociação ──────────────────────────────────────────
  if (statusAction === 'desassociar') {
    // ── Loading ──────────────────────────────────────────────────────────────
    if (desassociarStep === 'loading') {
      return (
        <Drawer isOpen={isOpen} title="" onClose={handleCloseDesassociar}>
          <div className="ddt-feedback-body">
            <Loading type="page" description="Desassociando terminal..." />
          </div>
        </Drawer>
      );
    }

    // ── Sucesso ──────────────────────────────────────────────────────────────
    if (desassociarStep === 'success') {
      return (
        <Drawer
          isOpen={isOpen}
          title=""
          onClose={handleCloseDesassociar}
          footer={{
            buttonLayout: 'fill',
            primaryButton: {
              children: 'Fechar',
              onClick: handleCloseDesassociar,
            },
          }}
        >
          <div className="ddt-feedback-body">
            <div className="ddt-done">
              <div className="ddt-done-icon">
                <Icon icon={CheckCircle} size={32} weight="regular" />
              </div>
              <div className="ddt-done-text">
                <span className="ddt-done-title">Operação concluída com sucesso</span>
                <span className="ddt-done-desc">Acompanhe o status dos terminais na listagem.</span>
              </div>
            </div>
          </div>
        </Drawer>
      );
    }

    // ── Confirmar ────────────────────────────────────────────────────────────
    return (
      <Drawer
        isOpen={isOpen}
        title="Desassociar terminal"
        onClose={onClose}
        onBack={() => { setStatusAction(null); setConfirmed(false); setDesassociarStep('confirm'); }}
        footer={{
          primaryButton: {
            children: 'Desassociar terminal',
            disabled: !confirmed,
            onClick: handleDesassociar,
          },
          secondaryButton: {
            children: 'Cancelar',
            onClick: () => { setStatusAction(null); setConfirmed(false); },
          },
        }}
      >
        <div className="ddt-body ddt-desassociar-body">
          <Card>
            <div className="ddt-desassociar-seller">
              <div className="ddt-desassociar-seller-info">
                <Text variant="label-base">{terminal.seller}</Text>
                <Text variant="body-sm" className="ddt-info-label">ID  {terminal.sellerId}</Text>
              </div>
              <Pills label="Seller atual" state="default" size="sm" />
            </div>
          </Card>

          <div className="ddt-desassociar-arrow">
            <div className="ddt-desassociar-arrow-line" />
            <div className="ddt-desassociar-arrow-head" />
          </div>

          <div className="ddt-desassociar-pill">
            <Pills
              label={`Desassociando terminal ${terminal.serialNumber}`}
              state="accent"
              size="base"
              icon={DeviceMobileSlash}
            />
          </div>

          <div className="ddt-desassociar-confirm">
            <Checkbox
              checked={confirmed}
              onChange={(checked) => setConfirmed(checked)}
              size="sm"
            />
            <Text variant="body-sm">
              Confirmo que este terminal ficará em estoque e sem transacionar até associar a outro seller novamente.
            </Text>
          </div>
        </div>
      </Drawer>
    );
  }

  // ─── View: Associar a outro seller ──────────────────────────────────────────
  if (statusAction === 'associar-outro') {
    if (associarStep === 'loading') {
      return (
        <Drawer isOpen={isOpen} title="" onClose={handleCloseAssociar}>
          <div className="ddt-feedback-body">
            <Loading type="page" description="Associando novo seller..." />
          </div>
        </Drawer>
      );
    }

    if (associarStep === 'success') {
      return (
        <Drawer
          isOpen={isOpen}
          title=""
          onClose={handleCloseAssociar}
          footer={{
            buttonLayout: 'fill',
            primaryButton: { children: 'Fechar', onClick: handleCloseAssociar },
          }}
        >
          <div className="ddt-feedback-body">
            <div className="ddt-done">
              <div className="ddt-done-icon">
                <Icon icon={CheckCircle} size={32} weight="regular" />
              </div>
              <div className="ddt-done-text">
                <span className="ddt-done-title">Operação concluída com sucesso</span>
                <span className="ddt-done-desc">Acompanhe o status dos terminais na listagem.</span>
              </div>
            </div>
          </div>
        </Drawer>
      );
    }

    // ── Formulário ────────────────────────────────────────────────────────────
    return (
      <Drawer
        isOpen={isOpen}
        title="Alterar associação de terminal"
        onClose={onClose}
        onBack={() => { setStatusAction(null); setSellerQuery(''); setSellerSearchType('lojista'); setSelectedSeller(null); setConfirmedAssociar(false); }}
        footer={{
          primaryButton: {
            children: 'Associar novo seller',
            disabled: !selectedSeller || !confirmedAssociar,
            onClick: handleAssociar,
          },
          secondaryButton: {
            children: 'Cancelar',
            onClick: () => { setStatusAction(null); setSellerQuery(''); setSellerSearchType('lojista'); setSelectedSeller(null); setConfirmedAssociar(false);},
          },
        }}
      >
        <div className="ddt-body ddt-desassociar-body">
          {/* Seller atual */}
          <Card>
            <div className="ddt-desassociar-seller">
              <div className="ddt-desassociar-seller-info">
                <Text variant="label-base">{terminal.seller}</Text>
                <Text variant="body-sm" className="ddt-info-label">ID  {terminal.sellerId}</Text>
              </div>
              <Pills label="Seller atual" state="default" size="sm" />
            </div>
          </Card>

          {/* Seta + pill de ação */}
          <div className="ddt-desassociar-arrow">
            <div className="ddt-desassociar-arrow-line" />
            <div className="ddt-desassociar-arrow-head" />
          </div>
          <div className="ddt-desassociar-pill">
            <Pills
              label={`Alterando associação do terminal ${terminal.serialNumber} para:`}
              state="accent"
              size="base"
              icon={DeviceRotate}
            />
          </div>
          <div className="ddt-desassociar-arrow">
            <div className="ddt-desassociar-arrow-line" />
            <div className="ddt-desassociar-arrow-head" />
          </div>

          {/* Busca / seller selecionado */}
          {selectedSeller ? (
            <Card>
              <div className="ddt-desassociar-seller">
                <div className="ddt-desassociar-seller-info">
                  <Text variant="label-base">{selectedSeller.name}</Text>
                  <Text variant="body-sm" className="ddt-info-label">ID  {selectedSeller.id}</Text>
                </div>
                <Pills label="Novo seller" state="success" size="sm" />
              </div>
            </Card>
          ) : (
            <div className="ddt-seller-search">
              <div className="ddt-seller-search-row">
                <div className="search-input-wrapper" ref={sellerSearchBarRef}>
                  <div className="search-type-select">
                    <button
                      type="button"
                      className="search-type-btn"
                      onClick={() => setSellerTypeDropdownOpen((v) => !v)}
                    >
                      <Text variant="label-sm">{currentSellerSearchType.label}</Text>
                      <Icon icon={CaretDown} size={14} weight="bold" />
                    </button>
                    {sellerTypeDropdownOpen && (
                      <ul className="search-type-dropdown">
                        {SELLER_SEARCH_TYPE_OPTIONS.map((opt) => (
                          <li
                            key={opt.value}
                            className={`search-type-option${sellerSearchType === opt.value ? ' search-type-option--active' : ''}`}
                            onMouseDown={() => {
                              setSellerSearchType(opt.value);
                              setSellerTypeDropdownOpen(false);
                              setSellerQuery('');
                            }}
                          >
                            <Text variant="body-sm">{opt.label}</Text>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="search-divider" />

                  <Icon icon={MagnifyingGlass} size={16} weight="regular" className="search-icon" />
                  <input
                    className="search-input-native"
                    placeholder={currentSellerSearchType.placeholder}
                    value={sellerQuery}
                    onChange={(e) => setSellerQuery(e.target.value)}
                  />
                </div>
                {sellerQuery && (
                  <Button variant="ghost" size="sm" onClick={() => setSellerQuery('')}>
                    Cancelar
                  </Button>
                )}
              </div>
              {sellerResults.length > 0 && (
                <div className="ddt-seller-results">
                  {sellerResults.map((s) => {
                    const field = sellerFieldForSearchType(s, sellerSearchType);
                    const idx = field.toLowerCase().indexOf(sellerQuery.trim().toLowerCase());
                    const before = field.slice(0, idx);
                    const match  = field.slice(idx, idx + sellerQuery.trim().length);
                    const after  = field.slice(idx + sellerQuery.trim().length);
                    const highlighted = <>{before}<strong>{match}</strong>{after}</>;
                    return (
                      <div
                        key={s.id}
                        className="ddt-seller-result-item"
                        onClick={() => { setSelectedSeller(s); setSellerQuery(''); }}
                      >
                        <div className="ddt-history-cell">
                          <span className="ddt-history-primary">{sellerSearchType === 'lojista' ? highlighted : s.name}</span>
                          <span className="ddt-history-secondary">{sellerSearchType === 'lojista' ? s.id : highlighted}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Confirmação — só aparece quando seller selecionado */}
          {selectedSeller && (
            <div className="ddt-desassociar-confirm">
              <Checkbox
                checked={confirmedAssociar}
                onChange={(checked) => setConfirmedAssociar(checked)}
                size="sm"
              />
              <Text variant="body-sm">
                Confirmo que todas as transações deste terminal passarão a ser deste novo seller a partir de agora.
              </Text>
            </div>
          )}
        </div>
      </Drawer>
    );
  }

  // ─── View: Detalhes ──────────────────────────────────────────────────────────
  return (
    <Drawer
      isOpen={isOpen}
      title="Detalhes do Terminal"
      onClose={onClose}
      footer={terminal.status === 'Em estoque' ? {
        primaryButton: {
          children: 'Associar a um seller',
          onClick: () => setStatusAction('associar-outro'),
        },
      } : {
        primaryButton: {
          children: 'Desassociar',
          onClick: () => setStatusAction('desassociar'),
        },
        secondaryButton: {
          children: 'Associar a outro seller',
          onClick: () => setStatusAction('associar-outro'),
        },
      }}
    >
      <div className="ddt-body">

        {/* ── Dados do terminal ─────────────────────────────────────── */}
        <Section title="Dados do terminal">
          <Section.Row
            label="Status"
            pills={<Pills label={pillLabel} state={pillState} size="base" />}
          />
          <Section.Row
            label="Proprietário"
            pills={<Pills label={terminal.owner} state="default" variant={terminal.owner === 'Barte' ? 'fill' : 'light'} size="base" />}
          />
          <Section.Row label="Modelo" content={terminal.model} />
          <Section.Row label="Versão do aplicativo" content={terminal.posVersion} />
          <Section.Row label="Serial" content={terminal.serialNumber} />
          <Section.Row label="ID" content={terminal.terminalId} />
          <Section.Row label="Data última transação" content={terminal.lastTransaction} />
        </Section>

        {/* ── Dados do seller ───────────────────────────────────────── */}
        <Section title="Dados do seller">
          <Section.Row label="Seller" content={terminal.seller} />
          <Section.Row label="Seller ID" content={terminal.sellerId} />
          <Section.Row label="CNPJ" content={cnpj} />
          <Section.Row label="Razão social" content={razaoSocial} />
          <Section.Row label="Nome fantasia" content={nomeFantasia} />
        </Section>

        {/* ── Histórico ────────────────────────────────────────────── */}
        <Section title="Histórico do terminal">
          <HistoryTimeline events={buildTimelineEvents(terminal)} />
        </Section>

      </div>
    </Drawer>
  );
}
